import React, {
  lazy,
  Suspense,
} from 'react';

const AuthWrapper = lazy(() => import('./components/authWrapper.js'));
const ComponentAccordion = lazy(() => import('./components/componentAccordion.js'));
const ComponentAccordionItem = lazy(() => import('./components/componentAccordionItem.js'));
const ComponentButton = lazy(() => import('./components/componentButton.js'));
const ComponentColumnLayout = lazy(() => import('./components/componentColumnLayout.js'));
const ComponentContactTeaser = lazy(() => import('./components/componentContactTeaser.js'));
const ComponentCoverImage = lazy(() => import('./components/componentCoverImage.js'));
const ComponentDualHeadline = lazy(() => import('./components/componentDualHeadline.js'));
const ComponentErrorPage = lazy(() => import('./components/componentErrorPage.js'));
const ComponentGap = lazy(() => import('./components/componentGap.js'));
const ComponentGetInTouch = lazy(() => import('./components/componentGetInTouch.js'));
const ComponentGroupedAccordion = lazy(() => import('./components/componentGroupedAccordion.js'));
const ComponentInformationAndDownloads = lazy(() => import('./components/componentInformationAndDownloads.js'));
const ComponentLocaldev = lazy(() => import('./components/componentLocaldev.js'));
const ComponentLoginButton = lazy(() => import('./components/componentLoginButton.js'));
const ComponentNotificationBar = lazy(() => import('./components/componentNotificationBar.js'));
const ComponentPromoCard = lazy(() => import('./components/componentPromoCard.js'));
const ComponentServiceBenefitItem = lazy(() => import('./components/componentServiceBenefitItem.js'));
const ComponentServiceBenefits = lazy(() => import('./components/componentServiceBenefits.js'));
const ComponentServiceFeaturesOverview = lazy(() => import('./components/componentServiceFeatureOverview.js'));
const ComponentServiceFeatureOverviewItem = lazy(() => import('./components/componentServiceFeatureOverviewItem.js'));
const ComponentServiceInclude = lazy(() => import('./components/componentServiceInclude.js'));
const ComponentStage = lazy(() => import('./components/componentStage.js'));
const ComponentTermsOfUseTabs = lazy(() => import('./components/componentTermsOfUseTabs.js'));
const ComponentText = lazy(() => import('./components/componentText.js'));
const ComponentUpdateFeed = lazy(() => import('./components/componentUpdateFeed.js'));
const ComponentUpdateFeedItem = lazy(() => import('./components/componentUpdateFeedItem.js'));
const ComponentWelcomeCard = lazy(() => import('./components/componentWelcomeCard.js'));
const ComponentContainer = lazy(() => import('./components/componentContainer.js'));
const ComponentDataPointsTeaserCards = lazy(() => import('./components/componentDataPointsTeaserCards.js'));
const ComponentHelpStage = lazy(() => import('./components/componentHelpStage.js'));
const ComponentExternalUpdateFeed = lazy(() => import('./components/componentExternalUpdateFeed.js'));
const ComponentSearchResults = lazy(() => import('./components/componentSearchResults.js'));
const ComponentDependenciesTable = lazy(() => import('./components/componentDependenciesTable.js'));
const ComponentLegalDocument = lazy(() => import('./components/componentLegalDocument.js'));
const ComponentLegalDocuments = lazy(() => import('./components/componentLegalDocuments.js'));
const ComponentVerticalList = lazy(() => import('./components/componentVerticalList.js'));
const ComponentFeatureHighlight = lazy(() => import('./components/componentFeatureHighlight.js'));
const ComponentInfoCardGrid = lazy(() => import('./components/componentInfoCardGrid.js'));
const ComponentKeyFunctions = lazy(() => import('./components/componentKeyFunctions.js'));
const ComponentFeedbackCTA = lazy(() => import('./components/componentFeedbackCTA.js'));
/**
 * @typedef {import('react').LazyExoticComponent} LazyExoticComponent
 */
/**
 * @type {Map<string, LazyExoticComponent>}
 */
export const componentMap = new Map();
componentMap.set('authWrapper', AuthWrapper);
componentMap.set('componentLocaldev', ComponentLocaldev);
componentMap.set('componentGap', ComponentGap);
componentMap.set('componentButton', ComponentButton);
componentMap.set('componentCoverImage', ComponentCoverImage);
componentMap.set('componentPromoCard', ComponentPromoCard);
componentMap.set('componentAccordion', ComponentAccordion);
componentMap.set('componentGroupedAccordion', ComponentGroupedAccordion);
componentMap.set('atomAccordionItem', ComponentAccordionItem);
componentMap.set('componentWelcomeCard', ComponentWelcomeCard);
componentMap.set('componentNotificationBar', ComponentNotificationBar);
componentMap.set('componentText', ComponentText);
// @ts-ignore
componentMap.set('componentServiceInclude', ComponentServiceInclude);
componentMap.set('componentStage', ComponentStage);
componentMap.set('componentExternalUpdateFeed', ComponentExternalUpdateFeed);
componentMap.set('componentUpdateFeed', ComponentUpdateFeed);
componentMap.set('componentUpdateFeedItem', ComponentUpdateFeedItem);
componentMap.set('componentServiceBenefits', ComponentServiceBenefits);
componentMap.set('componentServiceBenefitItem', ComponentServiceBenefitItem);
componentMap.set('componentServiceFeaturesOverview', ComponentServiceFeaturesOverview);
componentMap.set('componentServiceFeatureOverviewItem', ComponentServiceFeatureOverviewItem);
componentMap.set('componentContactTeaser', ComponentContactTeaser);
componentMap.set('componentLoginButton', ComponentLoginButton);
componentMap.set('componentErrorPage', ComponentErrorPage);
componentMap.set('componentColumnLayout', ComponentColumnLayout);
componentMap.set('componentGetInTouch', ComponentGetInTouch);
componentMap.set('componentInformationAndDownloads', ComponentInformationAndDownloads);
componentMap.set('componentDualHeadline', ComponentDualHeadline);
componentMap.set('componentTermsOfUseTabs', ComponentTermsOfUseTabs);
componentMap.set('componentContainer', ComponentContainer);
componentMap.set('componentDataPointsTeaserCards', ComponentDataPointsTeaserCards);
componentMap.set('componentHelpStage', ComponentHelpStage);
componentMap.set('componentSearchResults', ComponentSearchResults);
componentMap.set('componentDependenciesTable', ComponentDependenciesTable);
componentMap.set('componentLegalDocument', ComponentLegalDocument);
componentMap.set('componentLegalDocuments', ComponentLegalDocuments);
componentMap.set('componentVerticalList', ComponentVerticalList);
componentMap.set('componentFeatureHighlight', ComponentFeatureHighlight);
componentMap.set('componentInfoCardGrid', ComponentInfoCardGrid);
componentMap.set('componentKeyFunctions', ComponentKeyFunctions);
componentMap.set('componentFeedbackCTA', ComponentFeedbackCTA);

/**
 * @typedef {import('../../server/server.d.ts').ContentPage} ContentPage
 * @typedef {import('../../server/server.d.ts').ContentDodoComponent} ContentDodoComponent
 * @typedef {import('../../server/server.d.ts').ContentDodoComponents} ContentDodoComponents
 */
/**
 * @param {ContentPage} apiData
 */
const extractComponentsFromApiData = (apiData) => {
  return apiData?.fields?.components ?? [];
};

/**
 * @param {[string, ContentDodoComponent]} item
 * @param {ContentPage} apiData
 * @param {number} index
 */
const cmsComponentToReactComponent = (item, apiData, index) => {
  const [id, entry] = item;
  const Component = componentMap.get(id);
  let children = null;

  if (entry.fields?.components && Array.isArray(entry.fields.components)) {
    // eslint-disable-next-line no-use-before-define
    children = entry.fields.components.reduce(cmsComponentsToReactIterator(apiData), []);
  }

  const lang = apiData.sys.locale;
  // DO NOT MOVE key TO THE END
  // I've spent 3 hours on this, it explodes hydration
  // I DO NOT know why.
  // No I do know: https://github.com/parcel-bundler/parcel/issues/7755#event-6263524664
  // Remove this comment when this is released.
  const key = `${entry?.sys?.id}-${index}`;
  if (!children) {
    return (
      <Suspense fallback={null}>
        <Component key={key} lang={lang} {...entry.fields} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <Component key={key} lang={lang} {...entry.fields}>
        {children}
      </Component>
    </Suspense>
  );
};

/**
 * @param {ContentPage} apiData
 */
const cmsComponentsToReactIterator = (apiData) => (acc, curr, index) => {
  // Warning, this is recurrent! Changing signature must
  // also be updated in cmsComponentToReactComponent.
  const type = curr?.type;

  if (!type) {
    return acc;
  }

  if (!componentMap.has(type)) {
    console.warn(`Component ${type} is not implemented`);
    return acc;
  }

  const children = cmsComponentToReactComponent([type, curr], apiData, index);
  acc.push(children);

  return acc;
};

/**
 * @param {ContentPage} apiData
 */
export const getReactComponentFromApiData = (apiData) => {
  return extractComponentsFromApiData(apiData).reduce(cmsComponentsToReactIterator(apiData), []);
};
